import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NAV } from "@/config/nav";

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [module, setModule] = useState("");
  const [loading, setLoading] = useState(true);
  const timer = useRef();

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => { setDebounced(search); setPage(1); }, 350); return () => clearTimeout(timer.current); }, [search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/audit", { params: { page, page_size: 25, search: debounced, module: module || undefined } });
        setRows(data.items || []); setTotal(data.total || 0);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    };
    load();
  }, [page, debounced, module]);

  const columns = [
    { key: "created_at", label: "Timestamp", render: (r) => <span className="tabular text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</span> },
    { key: "user_email", label: "User", render: (r) => <div><div className="font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.user_email}</div></div> },
    { key: "module", label: "Module", render: (r) => <span className="rounded-sm bg-secondary px-2 py-0.5 text-xs capitalize">{r.module}</span> },
    { key: "action", label: "Action", render: (r) => <span className="font-medium capitalize">{String(r.action).replace(/_/g, " ")}</span> },
    { key: "record_label", label: "Record", render: (r) => r.record_label || r.record_id?.slice(0, 8) },
    { key: "reason", label: "Reason", render: (r) => r.reason || "—" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Audit Logs" subtitle="Immutable trail: who, what, outlet, before/after and when." />
      <DataTable
        columns={columns} rows={rows} loading={loading} total={total} page={page} pageSize={25} onPageChange={setPage}
        search={search} onSearch={setSearch} searchPlaceholder="Search user, action, record…"
        emptyTitle="No audit entries"
        toolbar={
          <Select value={module || "all"} onValueChange={(v) => { setModule(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-44" data-testid="audit-module"><SelectValue placeholder="All modules" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All modules</SelectItem>{NAV.map((n) => <SelectItem key={n.perm.split(":")[0]} value={n.perm.split(":")[0]}>{n.label}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
    </div>
  );
}
